/**
 * useContactChangeEffects — wraps ContactChangeOrchestrator for React consumers.
 *
 * Lets CifEditPanel dispatch the 3-stage ChangeOfContact chain with one call:
 *
 *   const effects = useContactChangeEffects();
 *   // inside handleConfirm, AFTER CustMod / cascade events have landed:
 *   await effects.runContactChangeEffects({
 *     cifId, before, after, officerCode, diff,
 *   });
 *
 * `orchestrator` is memoized against the current ApiClient so that identity
 * is stable across renders — CifEditPanel includes it in its handleConfirm
 * deps without triggering re-subscription.
 *
 * Ported from SPFx inline logic in CifUpdateScreen.tsx:328-362 (insider fraud
 * detection) and cif-update-workflow.md missing-items #1 + #2 (30-day tickler,
 * address notice).
 */
import { useMemo } from 'react';
import type { ChangeDiffEntry } from './useChangeTracker';
import { useApi } from './useApi';
import {
  ContactChangeOrchestrator,
  type ContactChangeRunInput,
  type ContactChangeRunResult,
} from '../services/ContactChangeOrchestrator';

export interface UseContactChangeEffects {
  orchestrator: ContactChangeOrchestrator;
  runContactChangeEffects: (input: ContactChangeRunInput) => Promise<ContactChangeRunResult>;
}

export function useContactChangeEffects(): UseContactChangeEffects {
  const { client } = useApi();
  const orchestrator = useMemo(() => new ContactChangeOrchestrator(client), [client]);
  return {
    orchestrator,
    runContactChangeEffects: (input: ContactChangeRunInput) =>
      orchestrator.runContactChangeEffects(input),
  };
}

export type { ContactChangeRunInput, ContactChangeRunResult, ChangeDiffEntry };
